package com.siriusxm.pia.rest.unifiedaggregator

import com.siriusxm.cmc.utils.getString
import contentingestion.unifiedmodel.Image
import contentingestion.unifiedmodel.ImageAspectRatio
import contentingestion.unifiedmodel.ImagePurpose
import contentingestion.unifiedmodel.ImageVariant
import kotlinx.serialization.json.*
import kotlin.time.Instant

/**
 * Encapsulates an aggregator entity
 */
class Entity(val data: JsonObject) : Map<String, JsonElement> by data {
    val id: String get() = data.entityId!!
    val type: String get() = data.entityType!!
    val version: String get() = data.entityVersion!!

    // A common field
    val name: String? get() = data.getString("name")

    // the default text label to use in the UI for this item.
    val label: String get() = name ?: data.getString("term") ?: id

    /**
     * When the catalog (merged) entity was last updated. Only present when this entity was
     * fetched with `includeMetadata = true` (see `Aggregator.getEntityById`), which embeds it as
     * `_metadata.lastModified`.
     */
    val lastModified: Instant? get() = data["_metadata"]?.getString("lastModified")?.let { Instant.parse(it) }

    /**
     * Get the images data
     */
    val images: Map<ImagePurpose, Map<ImageAspectRatio, ImageVariant>> get() = data["images"]?.decodeImages().orEmpty()

    /**
     * Get the default thumbnail to use
     */
    val thumbnail: Image?
        get() {
            val flat = images.flatten()
            val oneByOne = flat.firstOrNull { it.ratio == ImageAspectRatio.ASPECT_1X1 }?: flat.firstOrNull()
            val oneByOneImage = oneByOne?.image

            if (oneByOneImage != null) {
                return oneByOneImage
            }
            val img = data["image"]?.decodeImage()
            if (img != null) {
                return img
            }
            return null
        }
}

fun JsonElement.asEntity(): Entity = Entity(jsonObject)

fun List<JsonElement>.asEntities(): List<Entity> {
    return map { it.asEntity() }
}

val imageDecoder = Json {
    ignoreUnknownKeys = true
}

fun JsonElement.decodeImages(): Map<ImagePurpose, Map<ImageAspectRatio, ImageVariant>>? {
    return try {
        imageDecoder.decodeFromJsonElement(this)
    } catch (e: Throwable) {
        null // perhaps just an incorrectly named field
    }
}

fun JsonElement.decodeImage(): Image? = when (this) {
    is JsonObject -> runCatching { imageDecoder.decodeFromJsonElement<Image>(this) }.getOrNull()
    is JsonPrimitive -> if (isString) {
        Image(url = content)
    } else null
    else -> null
}
